import React, { useState, useEffect } from "react";
import AthleteLayout from "../common/AthleteLayout";
import { useAthleteAuthContext } from "../login/useAthleteAuthContext";
import { Link } from "react-router-dom";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import API_URL from "../../api/API";
import ladyRun from "../../images/ASTproductpage.png";

export const AthletePurchaseRunrocketTool = () => {
  const { athlete } = useAthleteAuthContext();
  const { dispatch } = useAthleteAuthContext();

  const stripe = useStripe();
  const elements = useElements();

  const [testResult, setTestResult] = useState(null);

  const [template] = useState({
    title: "AST Speed Profile",
    price: 199,
    photo: null,
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isPurchased, setIsPurchased] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);


  const fetchTestResults = async () => {
    try {
      const response = await fetch(
        API_URL.GET_ATHLETE_RUNROCKET_TEST_RESULT + athlete.id,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${athlete.token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch test results");
      }

      const data = await response.json();
      setTestResult(data);
      console.log(data);
    } catch (error) {
      console.log(error.message);
    }
  };

  // Fetch purchase data
  const fetchTemplateDetail = async () => {
    try {
      const res = await fetch(`${API_URL.GET_ATHLETE_PURCHASE_DATA}${athlete.id}`, {
        headers: { Authorization: `Bearer ${athlete.token}` },
      });
  
      if (!res.ok) return;
  
      const json = await res.json();
  
      const purchaseData = json.data; // <- important, use .data
  
      if (purchaseData?.tools) {
        const purchasedTemplate = purchaseData.tools.find(
          (item) =>
            item.tool_name.toLowerCase() === "ast speed profile" &&
            item.payment_status?.toUpperCase() === "COMPLETED"
        );
  
        if (purchasedTemplate) setIsPurchased(true);
      }
    } catch (err) {
      console.error("Error fetching purchase:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  

  useEffect(() => {
    fetchTemplateDetail();
    fetchTestResults();
  }, []);

  const handleBuyClick = () => setShowPaymentForm(true);

  const handlePayment = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");

    if (!stripe || !elements) return;

    try {
      const intentRes = await fetch(API_URL.CREATE_PAYMENT_INTENT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${athlete.token}`,
        },
        body: JSON.stringify({
          amount: Math.round(template.price), // backend multiplies by 100
          currency: "usd",
          athlete_id: `${athlete.firstname} | ${athlete.email}`,
          tool_name: "AST Speed Profile",
          category: "tool",
        }),
      });

      const { clientSecret } = await intentRes.json();
      if (!clientSecret) throw new Error("Missing PaymentIntent client secret");

      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: { card: elements.getElement(CardElement) },
      });

      if (result.error) setError(result.error.message);
      else if (result.paymentIntent.status === "succeeded") {
        await fetch(API_URL.POST_ATHLETE_PURCHASE_TOOL_DATA + athlete.id, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${athlete.token}`,
          },
          body: JSON.stringify({
            tool_name: "AST Speed Profile",
            price: template.price,
            payment_transaction_id: result.paymentIntent.id,
            payment_logs: "Stripe inline payment success",
            payment_status: "COMPLETED",
          }),
        });

        setSuccessMessage(
          "Payment successful! You now have access to the AST Speed Profile Tool."
        );
        setIsPurchased(true);
        setShowPaymentForm(false);
      }
    } catch (err) {
      console.error("Payment error:", err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <AthleteLayout>
      <div className="bg-[#F6F6F6] h-full overflow-y-auto flex justify-center">
        <div className="w-full max-w-2xl p-5">
          {loading ? (
            <div className="text-center py-10">Loading template details...</div>
          ) : error ? (
            <div className="text-center py-10 text-red-500">Error: {error}</div>
          ) : (
            <>
              {/* Product Card */}
              <div className="bg-white w-full rounded-[15px] overflow-hidden relative shadow-md">
                <div className="relative h-[260px] sm:h-[300px] md:h-[360px] flex justify-center items-center">
                  <img
                    src={template.photo ? `${API_URL.SERVER_PICTURE_URL}${template.photo}` : ladyRun}
                    alt={template.title}
                    className="w-full h-full object-cover rounded-t-[15px]"
                  />
                  <Link to="/athlete/tools" className="absolute top-5 left-5">
                    <ArrowL />
                  </Link>
                </div>
              </div>

              {/* Product Info */}
              <div className="mt-6">
                <p className="text-black text-xl font-semibold">{template.title}</p>
                <p className="text-black text-lg font-medium mt-2">${template.price}</p>
                <div className="mt-6 space-y-5 text-black">
  <div>
    <h2 className="text-2xl font-bold">{template.title}</h2>
    <p className="mt-2 text-lg font-semibold text-darkYellow">
      Stop guessing what your athlete needs to get faster.
    </p>
  </div>

  <p className="leading-7 text-base">
    Too many athletes train hard without knowing what is actually limiting
    their acceleration. Generic workouts can waste valuable training time
    and make it difficult for athletes and parents to know whether real
    progress is being made.
  </p>

  <p className="leading-7 text-base">
    The <strong>AST Speed Profile™</strong> tests how an athlete performs
    under increasing resistance, identifies their primary
    speed-development focus, and delivers a personalized
    <strong> 8-week training program</strong> based on their results.
  </p>

  <p className="leading-7 text-base">
    Athletes receive specific resistance levels, target speeds, reps,
    distances, and rest periods so every training session has a purpose.
  </p>

  <div className="bg-[#f8f3e3] border border-darkYellow rounded-xl p-5">
    <p className="font-bold text-lg mb-2">
      Know what to improve. Train with purpose. Measure the difference.
    </p>

    <p className="font-extrabold tracking-[0.25em] uppercase text-darkYellow">
      TEST. PROFILE. TRAIN.
    </p>
  </div>

  <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-4">
    <h3 className="font-semibold text-red-700 mb-2">
      Equipment Requirement
    </h3>

    <p className="text-sm leading-6">
      A <strong>Run Rocket</strong> resistance training system is required
      to complete the AST Speed Profile test and prescribed
      <strong> 8-week training program</strong>. Run Rocket equipment is
      <strong> not included</strong> with the purchase of the
      <strong> AST Speed Profile™</strong>.
    </p>
  </div>
</div>
               
              </div>

              {successMessage && (
                <div className="bg-green-500 text-white p-4 rounded-md mt-4 text-center">
                  {successMessage}
                </div>
              )}

              {/* Payment / Access */}
              <div className="w-full max-w-lg mt-8">
                {showPaymentForm ? (
                  <form onSubmit={handlePayment} className="space-y-4">
                    <CardElement className="p-4 border rounded-md bg-white" />
                    {error && <p className="text-red-500 text-sm">{error}</p>}
                    <button
                      type="submit"
                      disabled={!stripe || saving}
                      className="p-4 bg-black text-white rounded-full w-full disabled:opacity-50"
                    >
                      {saving ? "Processing..." : "Pay Now"}
                    </button>
                  </form>
                ) : isPurchased ? (
                  <Link
                  to={
                    testResult?.data?.rsquared >= 95
                    ? `/athlete/tools/runrocket/training-weeks/${testResult?.data?.training_focus_template_id}`
                      : "/athlete/tools/runrocket/test/step1"
                  }
                  className="p-6 border border-black text-base font-semibold hover:opacity-70 rounded-[70px] w-full text-center block"
                >
                  Access Now
                </Link>
                ) : (
                  <button
                    onClick={handleBuyClick}
                    className="p-6 border border-black text-base font-semibold hover:opacity-70 rounded-[70px] w-full"
                  >
                    Buy Now
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </AthleteLayout>
  );
};

const ArrowL = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="8" height="14" viewBox="0 0 8 14" fill="none">
    <path d="M7 13L1 7L7 1" stroke="black" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
